/**
 * StaffRolesManagement - Unified view of all staff roles across modules
 * Now supports creating custom roles for Security, Dining, and Housing
 */
import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Shield, ChefHat, Building2, Plus, Edit2, Trash2, Lock, 
  AlertTriangle, Loader2, Users, UtensilsCrossed
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Permission definitions for each module
const PERMISSIONS = {
  security: [
    { id: 'patrol', label: 'Patrol Rounds', description: 'Conduct patrol rounds' },
    { id: 'visitors', label: 'Visitor Management', description: 'Register and manage visitors' },
    { id: 'vehicles', label: 'Vehicle Management', description: 'Manage vehicle entry/exit' },
    { id: 'cameras', label: 'Camera Access', description: 'View security cameras' },
    { id: 'reports', label: 'View Reports', description: 'View security reports' },
    { id: 'manage_officers', label: 'Manage Officers', description: 'Manage other officers' },
  ],
  kitchen: [
    { id: 'kds.view_orders', label: 'View Orders', description: 'See incoming orders on KDS' },
    { id: 'kds.update_status', label: 'Update Order Status', description: 'Mark orders as preparing/ready' },
    { id: 'kds.manage_recipes', label: 'Manage Recipes', description: 'Create and edit recipes' },
    { id: 'kds.view_stock', label: 'View Stock', description: 'Check ingredient inventory' },
    { id: 'kds.create_requisitions', label: 'Create Requisitions', description: 'Request stock refills' },
    { id: 'kds.manage_staff', label: 'Manage Kitchen Staff', description: 'Assign tasks to staff' },
  ],
  pos: [
    { id: 'pos.create_orders', label: 'Create Orders', description: 'Take customer orders' },
    { id: 'pos.apply_discounts', label: 'Apply Discounts', description: 'Apply discounts to orders' },
    { id: 'pos.process_payments', label: 'Process Payments', description: 'Accept payments' },
    { id: 'pos.refunds', label: 'Process Refunds', description: 'Issue refunds' },
    { id: 'pos.view_reports', label: 'View Reports', description: 'Access sales reports' },
    { id: 'pos.void_orders', label: 'Void Orders', description: 'Cancel/void orders' },
    { id: 'pos.manage_staff', label: 'Manage POS Staff', description: 'Manage cashier assignments' },
  ],
  housing: [
    { id: 'housing.view_rooms', label: 'View Rooms', description: 'See room availability and status' },
    { id: 'housing.assign_rooms', label: 'Assign Rooms', description: 'Assign guests to rooms' },
    { id: 'housing.check_in', label: 'Check-In Guests', description: 'Process guest arrivals' },
    { id: 'housing.check_out', label: 'Check-Out Guests', description: 'Process guest departures' },
    { id: 'housing.manage_requests', label: 'Handle Requests', description: 'Manage service requests' },
    { id: 'housing.housekeeping', label: 'Housekeeping', description: 'Update room cleaning status' },
    { id: 'housing.manage_staff', label: 'Manage Housing Staff', description: 'Assign staff duties' },
    { id: 'housing.view_reports', label: 'View Reports', description: 'Access occupancy reports' },
  ]
};

// Color options
const ROLE_COLORS = [
  '#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444', '#EC4899', '#6366F1', '#14B8A6'
];

// Role Form Component
const RoleForm = ({ initialData, moduleType, staffType, permissionsList, onSubmit, onCancel, isLoading }) => {
  const [name, setName] = useState(initialData?.name || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [permissions, setPermissions] = useState(initialData?.permissions || []);
  const [color, setColor] = useState(initialData?.color || '#3B82F6');
  const [selectedStaffType, setSelectedStaffType] = useState(staffType || initialData?.staff_type || 'kitchen');

  const togglePermission = (permId) => {
    setPermissions(prev => 
      prev.includes(permId) ? prev.filter(p => p !== permId) : [...prev, permId]
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Role name is required');
      return;
    }
    const data = { name, description, permissions, color };
    if (moduleType === 'dining') {
      data.staff_type = selectedStaffType;
    }
    onSubmit(data);
  };

  // Get the correct permissions list
  const currentPermissions = moduleType === 'dining' 
    ? (selectedStaffType === 'kitchen' ? PERMISSIONS.kitchen : PERMISSIONS.pos)
    : permissionsList;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label>Role Name *</Label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Shift Supervisor"
          data-testid="input-role-name"
        />
      </div>

      <div>
        <Label>Description</Label>
        <Input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Brief description of this role"
        />
      </div>

      {moduleType === 'dining' && !initialData && (
        <div>
          <Label>Staff Type</Label>
          <Select value={selectedStaffType} onValueChange={setSelectedStaffType}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="kitchen">Kitchen Staff</SelectItem>
              <SelectItem value="pos">POS/Cashier Staff</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      <div>
        <Label>Badge Color</Label>
        <div className="flex gap-2 mt-2">
          {ROLE_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              className={`w-8 h-8 rounded-full border-2 transition-all ${
                color === c ? 'border-gray-800 scale-110' : 'border-transparent'
              }`}
              style={{ backgroundColor: c }}
              onClick={() => setColor(c)}
            />
          ))}
        </div>
      </div>

      <div>
        <Label>Permissions</Label>
        <div className="space-y-2 mt-2 max-h-48 overflow-y-auto">
          {currentPermissions.map((perm) => (
            <div
              key={perm.id}
              className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                permissions.includes(perm.id)
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => togglePermission(perm.id)}
            >
              <Checkbox
                checked={permissions.includes(perm.id)}
                onCheckedChange={() => togglePermission(perm.id)}
              />
              <div>
                <span className="font-medium text-sm">{perm.label}</span>
                <p className="text-xs text-gray-500">{perm.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" disabled={isLoading} data-testid="submit-role-btn">
          {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          {initialData ? 'Update' : 'Create'} Role
        </Button>
      </DialogFooter>
    </form>
  );
};

// Role Card Component
const RoleCard = ({ role, isSystem, canEdit, onEdit, onDelete }) => (
  <Card className={isSystem ? 'bg-gray-50' : ''}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: role.color || '#3B82F6' }} />
          <span className="font-medium">{role.name}</span>
        </div>
        {isSystem ? (
          <Badge variant="secondary" className="text-xs"><Lock className="h-3 w-3 mr-1" />System</Badge>
        ) : canEdit && (
          <div className="flex gap-1">
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => onEdit(role)}>
              <Edit2 className="h-3 w-3" />
            </Button>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => onDelete(role)}>
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        )}
      </div>
      {role.description && <p className="text-sm text-gray-500 mb-2">{role.description}</p>}
      <div className="flex flex-wrap gap-1">
        {(role.permissions || []).slice(0, 4).map((perm) => (
          <Badge key={perm} variant="outline" className="text-xs">{perm.split('.').pop()}</Badge>
        ))}
        {(role.permissions || []).length > 4 && (
          <Badge variant="outline" className="text-xs">+{role.permissions.length - 4}</Badge>
        )}
      </div>
    </CardContent>
  </Card>
);

const StaffRolesManagement = () => {
  const queryClient = useQueryClient();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [activeModule, setActiveModule] = useState('security'); // security, dining, housing

  const token = getToken();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Fetch security roles
  const { data: securityRoles = [], isLoading: loadingSecurityRoles } = useQuery({
    queryKey: ['security-roles'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/security-roles`, { headers });
      if (!res.ok) return [];
      return res.json();
    }
  });

  // Fetch dining roles
  const { data: diningRolesData, isLoading: loadingDiningRoles } = useQuery({
    queryKey: ['dining-roles'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/dining/roles`, { headers });
      if (!res.ok) return { kitchen_roles: [], pos_roles: [] };
      return res.json();
    }
  });

  // Fetch housing roles
  const { data: housingRolesData, isLoading: loadingHousingRoles } = useQuery({
    queryKey: ['housing-roles'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/accommodation/roles`, { headers });
      if (!res.ok) return { housing_roles: [] };
      return res.json();
    }
  });

  // Generic create mutation
  const createMutation = useMutation({
    mutationFn: async ({ module, data }) => {
      const endpoints = {
        security: '/api/admin/security-roles',
        dining: '/api/admin/dining/roles',
        housing: '/api/admin/accommodation/roles'
      };
      const res = await fetch(`${API_URL}${endpoints[module]}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to create role');
      }
      return res.json();
    },
    onSuccess: (_, { module }) => {
      const queryKeys = {
        security: ['security-roles'],
        dining: ['dining-roles'],
        housing: ['housing-roles']
      };
      queryClient.invalidateQueries({ queryKey: queryKeys[module] });
      setShowAddDialog(false);
      toast.success('Role created successfully');
    },
    onError: (error) => toast.error(error.message)
  });

  // Generic update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ module, id, data }) => {
      const endpoints = {
        security: `/api/admin/security-roles/${id}`,
        dining: `/api/admin/dining/roles/${id}`,
        housing: `/api/admin/accommodation/roles/${id}`
      };
      const res = await fetch(`${API_URL}${endpoints[module]}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to update role');
      }
      return res.json();
    },
    onSuccess: (_, { module }) => {
      const queryKeys = {
        security: ['security-roles'],
        dining: ['dining-roles'],
        housing: ['housing-roles']
      };
      queryClient.invalidateQueries({ queryKey: queryKeys[module] });
      setEditingRole(null);
      toast.success('Role updated');
    },
    onError: (error) => toast.error(error.message)
  });

  // Generic delete mutation
  const deleteMutation = useMutation({
    mutationFn: async ({ module, id }) => {
      const endpoints = {
        security: `/api/admin/security-roles/${id}`,
        dining: `/api/admin/dining/roles/${id}`,
        housing: `/api/admin/accommodation/roles/${id}`
      };
      const res = await fetch(`${API_URL}${endpoints[module]}`, {
        method: 'DELETE',
        headers
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to delete role');
      }
      return res.json();
    },
    onSuccess: (_, { module }) => {
      const queryKeys = {
        security: ['security-roles'],
        dining: ['dining-roles'],
        housing: ['housing-roles']
      };
      queryClient.invalidateQueries({ queryKey: queryKeys[module] });
      setDeleteConfirm(null);
      toast.success('Role deleted');
    },
    onError: (error) => toast.error(error.message)
  });

  const handleCreateSubmit = useCallback((data) => {
    createMutation.mutate({ module: activeModule, data });
  }, [activeModule, createMutation]);

  const handleUpdateSubmit = useCallback((data) => {
    if (editingRole) {
      updateMutation.mutate({ module: editingRole.module, id: editingRole.id, data });
    }
  }, [editingRole, updateMutation]);

  const kitchenRoles = diningRolesData?.kitchen_roles || [];
  const posRoles = diningRolesData?.pos_roles || [];
  const housingRoles = housingRolesData?.housing_roles || [];

  // Render role section with system and custom roles
  const renderRoleSection = (roles, moduleType, staffType = null, icon, title) => {
    const systemRoles = roles.filter(r => r.is_system);
    const customRoles = roles.filter(r => !r.is_system);

    return (
      <div className="space-y-4">
        {/* System Roles */}
        {systemRoles.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-slate-500 mb-2 flex items-center gap-2">
              {icon}
              {title} - Default Roles
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {systemRoles.map(role => (
                <RoleCard key={role.id} role={role} isSystem={true} />
              ))}
            </div>
          </div>
        )}

        {/* Custom Roles */}
        <div>
          <h4 className="text-sm font-medium text-slate-500 mb-2">
            {title} - Custom Roles
          </h4>
          {customRoles.length === 0 ? (
            <div className="text-center py-4 text-slate-400 border-2 border-dashed rounded-lg text-sm">
              No custom roles yet
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {customRoles.map(role => (
                <RoleCard 
                  key={role.id} 
                  role={role} 
                  canEdit={true}
                  onEdit={(r) => setEditingRole({ ...r, module: moduleType, staffType })}
                  onDelete={(r) => setDeleteConfirm({ ...r, module: moduleType })}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="staff-roles-management">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6 text-indigo-600" />
            Staff Roles Management
          </h2>
          <p className="text-sm text-slate-500">
            Custom role definitions for Security, Dining, Housing.
            For ecosystem-wide people management use{' '}
            <a href="/admin/ecosystem-hr" className="text-indigo-600 underline">Ecosystem HR</a>.
          </p>
        </div>
      </div>

      {/* Cross-module hub — for team management outside these 3 hospitality modules */}
      <details className="border border-slate-200 rounded-lg bg-slate-50" data-testid="staff-roles-cross-module-hub">
        <summary className="cursor-pointer px-4 py-3 text-sm font-semibold text-slate-700 flex items-center gap-2">
          <Users className="h-4 w-4" /> Other modules manage their own teams — expand for links
        </summary>
        <div className="p-4 grid md:grid-cols-2 gap-2 text-xs">
          <a href="/admin/shop" className="border rounded p-2 hover:bg-white">
            <b>Bookshop team</b><div className="text-slate-500">Invites, roles, permissions for bookshop staff</div>
          </a>
          <a href="/admin/events" className="border rounded p-2 hover:bg-white">
            <b>Event team</b><div className="text-slate-500">Volunteers, ushers, check-in roles</div>
          </a>
          <a href="/admin/fwtv" className="border rounded p-2 hover:bg-white">
            <b>Faith Works TV</b><div className="text-slate-500">Producers, editors, transcribers</div>
          </a>
          <a href="/admin/school" className="border rounded p-2 hover:bg-white">
            <b>School HR</b><div className="text-slate-500">Teachers, admin staff, HR intelligence</div>
          </a>
          <a href="/admin/church" className="border rounded p-2 hover:bg-white">
            <b>Church module</b><div className="text-slate-500">Elders, ministers, ministry heads</div>
          </a>
          <a href="/admin/ecosystem-hr" className="border rounded p-2 hover:bg-white border-indigo-300">
            <b className="text-indigo-700">Ecosystem HR (master directory) →</b>
            <div className="text-slate-500">Cross-scope people index + dedupe + employee data</div>
          </a>
        </div>
      </details>

      <Tabs value={activeModule} onValueChange={setActiveModule} className="space-y-4">
        <TabsList>
          <TabsTrigger value="security" className="flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Security ({securityRoles.length})
          </TabsTrigger>
          <TabsTrigger value="dining" className="flex items-center gap-2">
            <ChefHat className="h-4 w-4" />
            Dining ({kitchenRoles.length + posRoles.length})
          </TabsTrigger>
          <TabsTrigger value="housing" className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            Housing ({housingRoles.length})
          </TabsTrigger>
        </TabsList>

        {/* Security Roles Tab */}
        <TabsContent value="security">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Security Roles</CardTitle>
                  <CardDescription>Define roles for security officers and guards</CardDescription>
                </div>
                <Button onClick={() => setShowAddDialog(true)} data-testid="add-security-role-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Role
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingSecurityRoles ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                </div>
              ) : (
                renderRoleSection(securityRoles, 'security', null, <Shield className="h-4 w-4" />, 'Security')
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Dining Roles Tab */}
        <TabsContent value="dining">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Dining Staff Roles</CardTitle>
                  <CardDescription>Define roles for kitchen and POS staff</CardDescription>
                </div>
                <Button onClick={() => setShowAddDialog(true)} data-testid="add-dining-role-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Role
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {loadingDiningRoles ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                </div>
              ) : (
                <>
                  {renderRoleSection(kitchenRoles, 'dining', 'kitchen', <UtensilsCrossed className="h-4 w-4" />, 'Kitchen')}
                  <hr className="my-4" />
                  {renderRoleSection(posRoles, 'dining', 'pos', <Users className="h-4 w-4" />, 'POS/Cashier')}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Housing Roles Tab */}
        <TabsContent value="housing">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Housing Staff Roles</CardTitle>
                  <CardDescription>Define roles for accommodation and front desk staff</CardDescription>
                </div>
                <Button onClick={() => setShowAddDialog(true)} data-testid="add-housing-role-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Role
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingHousingRoles ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                </div>
              ) : (
                renderRoleSection(housingRoles, 'housing', null, <Building2 className="h-4 w-4" />, 'Housing')
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Role Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Create {activeModule === 'security' ? 'Security' : activeModule === 'dining' ? 'Dining' : 'Housing'} Role
            </DialogTitle>
            <DialogDescription>Define a new role with custom permissions</DialogDescription>
          </DialogHeader>
          {showAddDialog && (
            <RoleForm
              moduleType={activeModule}
              permissionsList={
                activeModule === 'security' ? PERMISSIONS.security :
                activeModule === 'housing' ? PERMISSIONS.housing :
                PERMISSIONS.kitchen
              }
              onSubmit={handleCreateSubmit}
              onCancel={() => setShowAddDialog(false)}
              isLoading={createMutation.isPending}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={!!editingRole} onOpenChange={(open) => !open && setEditingRole(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Role</DialogTitle>
          </DialogHeader>
          {editingRole && (
            <RoleForm
              key={editingRole.id}
              initialData={editingRole}
              moduleType={editingRole.module}
              staffType={editingRole.staffType}
              permissionsList={
                editingRole.module === 'security' ? PERMISSIONS.security :
                editingRole.module === 'housing' ? PERMISSIONS.housing :
                editingRole.staffType === 'kitchen' ? PERMISSIONS.kitchen : PERMISSIONS.pos
              }
              onSubmit={handleUpdateSubmit}
              onCancel={() => setEditingRole(null)}
              isLoading={updateMutation.isPending}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Delete Role
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &quot;{deleteConfirm?.name}&quot;? Staff with this role will need to be reassigned.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button 
              variant="destructive" 
              onClick={() => deleteMutation.mutate({ module: deleteConfirm.module, id: deleteConfirm.id })}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffRolesManagement;
